// --- FINAL, CORRECTED & REUSABLE FILE: packages/components/src/admin/tasks/TaskDetailView.tsx ---

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from '@dopomogai/ui';
import { X, ExternalLink, Star } from 'lucide-react';

import {
    Button,
    IconButton,
    TextField,
    TextareaField,
    SelectField
} from '@dopomogai/ui';
import { useUiStore } from '../../../../../../apps/website/src/stores/ui'; // Adjust path
import type { ProjectTask, TaskPayload } from '@dopomogai/types';

interface TaskDetailViewProps {
  /** The full task data object to display and edit. */
  task: ProjectTask;
  /** Callback to close this detail view. */
  onClose: () => void;
  /** 
   * A generic callback to handle the save action. It provides the final TaskPayload.
   * This makes the component reusable for both creating and updating tasks.
   */
  onSave: (payload: TaskPayload) => Promise<void>;
  /** Indicates if a save operation is currently in progress. */
  isSaving: boolean;
  /** Optional override for the primary save button's text key. */
  saveButtonTextKey?: string;
}

/**
 * An inline, "quick edit" view for a task. Appears in place of a DraggableTaskCard
 * on a Kanban board, allowing for rapid edits without a disruptive modal.
 * It can be used for both creating and updating a task.
 */
export const TaskDetailView: React.FC<TaskDetailViewProps> = ({
  task,
  onClose,
  onSave,
  isSaving,
  saveButtonTextKey = 'common:saveChanges', // Default text for "update" scenarios
}) => {
  const { i18n, t } = useTranslation(['admin_tasks', 'common']);
  const { openTaskInspector } = useUiStore();

  const [draftTask, setDraftTask] = useState<ProjectTask>(task);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // When the parent component passes a new task, reset the draft and changes.
    // This is important for the inline-creation flow.
    setDraftTask(task);
    setHasChanges(false); 
  }, [task]);
  
  const handleDataChange = (data: Partial<ProjectTask>) => {
    setDraftTask(prev => ({ ...prev, ...data }));
    setHasChanges(true);
  };
  
  const handleSaveChanges = async () => {
    // The component is now generic; it just constructs the payload and calls the provided onSave function.
    const payload: TaskPayload = {
      _id: draftTask._id, // The ID is used by the backend to identify the task to update/create
        status: draftTask.status,
        task_type: draftTask.task_type,
        points: draftTask.points,
        links: draftTask.links,
        assignee_ids: draftTask.assignee_ids,
        checklist: (draftTask as any).checklist, // Include checklist if present
        translations: {
            [i18n.language]: {
                title: draftTask.title,
                description: draftTask.description,
            }
        }
    };
    
    // The parent component's `onSave` function now controls the full save lifecycle.
    await onSave(payload);
  };
  
  const handleDiscardChanges = () => {
    setDraftTask(task);
    setHasChanges(false);
  };

  const openFullEditor = () => {
    openTaskInspector(draftTask, { 
      projectId: draftTask.project_id, 
      sprintId: draftTask._id ?? undefined 
    });
  };

  return (
    // The `layout` prop from framer-motion creates the "magic motion" expand effect
    <motion.div layoutId={`task-card-${task._id}`} className="bg-white rounded-xl border-2 border-black shadow-lg flex flex-col">
      {/* Header */}
      <div className="p-4 border-b-2 border-black flex justify-between items-center bg-slate-50">
        <div>
          {/* For new tasks, the ID is temporary and not worth showing. */}
          {!task.created_at && <p className="text-sm font-semibold text-slate-500">{t('editor.inline.newTask')}</p>}
        </div>
        <div className="flex items-center gap-2">
            <Button 
                textKey="admin_tasks:editor.openFullEditor" 
                variant="outline"
                size="sm"
                icon={<ExternalLink size={14}/>}
                onClick={openFullEditor}
            />
            <IconButton icon={<X size={18}/>} onClick={onClose} ariaLabel={t('common:close')} />
        </div>
      </div>

      {/* Main Content & Sidebar */}
      <div className="flex flex-col md:flex-row">
        {/* Left: Title & Description */}
        <div className="p-5 flex-grow space-y-4">
            <TextareaField
                name="task-title-inline"
                value={draftTask.title}
                onChange={(e) => handleDataChange({ title: e.target.value })}
                className="text-xl font-bold !p-0 !border-0"
                textareaClassName="!text-xl !font-bold"
                placeholder={t('editor.titlePlaceholder')}
                autoFocus // Automatically focus the title field
            />
            <TextareaField
                name="task-description-inline"
                label={t('editor.descriptionLabel')}
                value={draftTask.description || ''}
                onChange={(e) => handleDataChange({ description: e.target.value })}
                rows={6}
                placeholder={t('editor.descriptionPlaceholder')}
            />
        </div>
        
        {/* Right: Properties */}
        <div className="p-5 border-t-2 md:border-t-0 md:border-l-2 border-black bg-slate-50/50 w-full md:w-64 flex-shrink-0 space-y-4">
            <SelectField
                name="task-status-inline"
                label={t('editor.statusLabel')}
                value={draftTask.status}
                onChange={val => handleDataChange({ status: val as ProjectTask['status'] })}
                options={[
                    {value: 'todo', label: 'Todo'},
                    {value: 'in-progress', label: 'In Progress'},
                    {value: 'done', label: 'Done'},
                    {value: 'blocked', label: 'Blocked'},
                    {value: 'archived', label: 'Archived'},
                ]}
            />
            <SelectField
                name="task-type-inline"
                label={t('editor.typeLabel')}
                value={draftTask.task_type}
                onChange={val => handleDataChange({ task_type: val as ProjectTask['task_type'] })}
                options={[
                    {value: 'feature', label: 'Feature'},
                    {value: 'bugfix', label: 'Bugfix'},
                    {value: 'chore', label: 'Chore'},
                    {value: 'docs', label: 'Docs'},
                    {value: 'research', label: 'Research'},
                ]}
            />
            <TextField
                name="task-points-inline"
                label={t('editor.pointsLabel')}
                type="number"
                value={String(draftTask.points || '')}
                onChange={(e) => handleDataChange({ points: Number(e.target.value) || undefined })}
                leadingIcon={<Star size={18} className="text-slate-400" />}
            />
        </div>
      </div>
      
      {/* Footer with Save/Discard Actions */}
      {/* The footer is now always visible to provide the main save action */}
      <motion.div 
        className="overflow-hidden"
      >
        <div className="p-4 border-t-2 border-black bg-slate-50 flex justify-end items-center gap-3">
            {hasChanges && <p className="text-sm font-medium text-amber-700 mr-auto">{t('editor.unsavedChanges')}</p>}
            
            <Button textKey="common:cancel" variant="secondary" onClick={onClose} disabled={isSaving} />
            <Button
              textKey={saveButtonTextKey}
              onClick={handleSaveChanges}
              loading={isSaving}
              // A new task can't be saved without a title
              disabled={isSaving || (task.version < 2 && !draftTask.title.trim())}
            />
        </div>
      </motion.div>
    </motion.div>
  );
};